import { useState, useEffect } from "react";
import { supabase, BRAND } from "./constants";
import { useIsMobile } from "./lib/responsive";
import Logo from "./Logo";

// /welcome — aceptar la invitación a rutment.com: la persona pone su
// contraseña y entra. GEMELA de welcome/page.js del portal.
//
// El correo de invitación (/api/invite-client) enlaza aquí con
// ?token_hash=…&type=invite&lang=en. El token NO se gasta al abrir la página:
// se gasta al pulsar el botón (verifyOtp). Así los antivirus de correo que
// abren los enlaces para revisarlos no lo queman antes de que llegue la
// persona. type = 'recovery' cuando se reenvía a alguien que ya abrió el
// enlace anterior sin poner contraseña.
//
// Al guardar, accept_client_invite() (security definer) marca
// user_clients.invite_accepted_at de su usuario, y entra a la Home.

const MIN_LEN = 8;
const BG = "#0E0E12";
const PANEL = "#18181F";
const LINE_D = "#2A2A35";
const MUTED_D = "#888899";
const DANGER_D = "#EF4444";

export default function WelcomeView() {
  const isMobile = useIsMobile();
  const [lang, setLang] = useState("en");
  const [params, setParams] = useState(null);
  const [state, setState] = useState("loading"); // loading · form · invalid · expired
  const [verified, setVerified] = useState(false);
  const [password, setPassword] = useState("");
  const [repeat, setRepeat] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const es = lang === "es";

  useEffect(() => {
    const q = new URLSearchParams(window.location.search);
    const tokenHash = q.get("token_hash");
    const type = q.get("type") === "recovery" ? "recovery" : "invite";
    if (q.get("lang") === "es" || q.get("lang") === "en") setLang(q.get("lang"));
    if (tokenHash) { setParams({ tokenHash, type }); setState("form"); }
    else setState("invalid");
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < MIN_LEN) { setError(es ? `Mínimo ${MIN_LEN} caracteres.` : `At least ${MIN_LEN} characters.`); return; }
    if (password !== repeat) { setError(es ? "Las dos contraseñas no coinciden." : "The two passwords don't match."); return; }
    setSaving(true);
    // 1. Gastar el token (abre la sesión). Si ya se gastó en un intento
    //    anterior de esta página, la sesión sigue abierta y no se repite.
    if (!verified) {
      const { error: vErr } = await supabase.auth.verifyOtp({ token_hash: params.tokenHash, type: params.type });
      if (vErr) { setSaving(false); setState("expired"); return; }
      setVerified(true);
    }
    // 2. La contraseña
    const { error: pErr } = await supabase.auth.updateUser({ password });
    if (pErr) { setSaving(false); setError(pErr.message); return; }
    // 3. Invitación aceptada (y comprobar que tiene empresa)
    const { data, error: aErr } = await supabase.rpc("accept_client_invite");
    if (aErr || !data?.ok) {
      setSaving(false);
      setError(es
        ? "Tu contraseña está guardada, pero tu cuenta no tiene empresa asociada. Escribe a hello@rutment.com."
        : "Your password is saved, but your account has no company linked. Please write to hello@rutment.com.");
      return;
    }
    window.location.replace("/");
  };

  const field = { width: "100%", boxSizing: "border-box", background: BG, border: `1.5px solid ${error ? DANGER_D : LINE_D}`, borderRadius: 10, padding: "12px 16px", fontSize: isMobile ? 16 : 14, color: "#fff", outline: "none", fontFamily: "'DM Sans', sans-serif", ...(isMobile ? { minHeight: 48 } : null) };
  const primary = { width: "100%", background: saving ? "#555" : BRAND, color: "#fff", border: "none", borderRadius: 10, padding: "13px", fontSize: 15, fontWeight: 700, cursor: saving ? "not-allowed" : "pointer", fontFamily: "'DM Sans', sans-serif", ...(isMobile ? { minHeight: 50 } : null) };

  const shell = (children) => (
    <div style={{ minHeight: "100vh", background: BG, display: "flex", alignItems: isMobile ? "stretch" : "center", justifyContent: "center", padding: isMobile ? 0 : 24, boxSizing: "border-box", fontFamily: "'DM Sans', sans-serif" }}>
      <link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800&family=JetBrains+Mono:wght@700&display=swap" rel="stylesheet" />
      <div style={isMobile
        ? { background: PANEL, width: "100%", padding: "40px 16px 32px", boxSizing: "border-box" }
        : { background: PANEL, border: `1px solid ${LINE_D}`, borderRadius: 18, padding: "36px 40px", width: 380, boxSizing: "border-box", boxShadow: "0 32px 80px rgba(0,0,0,0.6)" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 24 }}>
          <Logo size={20} color="#fff" />
          <div style={{ display: "flex", gap: 4 }}>
            {["en", "es"].map(l => (
              <button key={l} onClick={() => setLang(l)} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 12, fontWeight: 700, color: lang === l ? "#fff" : MUTED_D, padding: isMobile ? "10px 8px" : "4px 6px", fontFamily: "inherit" }}>{l.toUpperCase()}</button>
            ))}
          </div>
        </div>
        {children}
      </div>
    </div>
  );

  if (state === "loading") return shell(null);

  if (state === "invalid" || state === "expired") return shell(
    <>
      <h1 style={{ fontSize: 22, fontWeight: 800, color: "#fff", margin: "0 0 8px" }}>
        {state === "expired" ? (es ? "El enlace ha caducado" : "This link has expired") : (es ? "Enlace no válido" : "Invalid link")}
      </h1>
      <p style={{ fontSize: 13, color: MUTED_D, margin: "0 0 24px", lineHeight: 1.6 }}>
        {state === "expired"
          ? (es ? "Los enlaces de invitación sirven una sola vez y caducan a las 24 horas. Responde al correo de invitación y te enviamos otro." : "Invitation links work only once and expire after 24 hours. Reply to the invitation email and we will send you a new one.")
          : (es ? "Abre el enlace del correo de invitación tal cual llegó. Si no lo encuentras, escribe a hello@rutment.com." : "Open the link in your invitation email exactly as it arrived. If you can't find it, write to hello@rutment.com.")}
      </p>
      <button onClick={() => window.location.replace("/")} style={primary}>{es ? "Ir a Entrar" : "Go to sign in"}</button>
    </>
  );

  return shell(
    <>
      <h1 style={{ fontSize: 22, fontWeight: 800, color: "#fff", margin: "0 0 6px" }}>{es ? "Bienvenido a Rutment" : "Welcome to Rutment"}</h1>
      <p style={{ fontSize: 13, color: MUTED_D, margin: "0 0 24px", lineHeight: 1.6 }}>
        {es ? "Crea tu contraseña para entrar en la plataforma de tu empresa." : "Create your password to access your company's platform."}
      </p>
      <form onSubmit={submit} style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        <input type="password" value={password} onChange={e => setPassword(e.target.value)} required autoComplete="new-password"
          aria-label={es ? "Contraseña nueva" : "New password"}
          placeholder={es ? `Contraseña (mínimo ${MIN_LEN} caracteres)` : `Password (at least ${MIN_LEN} characters)`}
          style={field} onFocus={e => { e.target.style.borderColor = BRAND; }} onBlur={e => { e.target.style.borderColor = error ? DANGER_D : LINE_D; }} />
        <input type="password" value={repeat} onChange={e => setRepeat(e.target.value)} required autoComplete="new-password"
          aria-label={es ? "Repite la contraseña" : "Repeat password"} placeholder={es ? "Repite la contraseña" : "Repeat password"}
          style={field} onFocus={e => { e.target.style.borderColor = BRAND; }} onBlur={e => { e.target.style.borderColor = error ? DANGER_D : LINE_D; }} />
        {/* Alto reservado: el botón no salta cuando aparece el error */}
        <div style={{ minHeight: 18, fontSize: 12, color: DANGER_D, lineHeight: 1.5 }}>{error}</div>
        <button type="submit" disabled={saving} style={primary}>
          {saving ? (es ? "Guardando…" : "Saving…") : (es ? "Crear contraseña y entrar" : "Create password and sign in")}
        </button>
      </form>
      <p style={{ fontSize: 12, color: MUTED_D, margin: "16px 0 0", lineHeight: 1.6 }}>
        {es ? "Después podrás entrar con este email y tu contraseña, o con Google si es el mismo email." : "Afterwards you can sign in with this email and your password, or with Google if it's the same email."}
      </p>
    </>
  );
}
